/**
 * Application Detail Page
 * Shows Application Journey at the top with full application details below
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { applicationsAPI } from '@/lib/api';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import JourneyTracker from '@/components/JourneyTracker';
import JourneyDocumentManager from '@/components/JourneyDocumentManager';
import StudentPersonalDocsPanel from '@/components/StudentPersonalDocsPanel';
import ActivityFeedTimeline from '@/components/ActivityFeedTimeline';
import DocumentsRequiredBanner from '@/components/DocumentsRequiredBanner';
import VisaProbabilityWidget from '@/components/VisaProbabilityWidget';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import {
  ArrowLeft,
  Loader2,
  User,
  Mail,
  Phone,
  GraduationCap,
  Building2,
  MapPin,
  Calendar,
  FileText,
  Send,
  Download,
  StickyNote,
  Clock,
  ExternalLink,
  DollarSign,
  AlertCircle
} from 'lucide-react';

const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'employee';
  const isAgent = user?.role === 'agent';
  const isStudent = user?.role === 'student';

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    setLoading(true);
    try {
      const response = await applicationsAPI.getById(id);
      setApplication(response.data);
      
      // Fetch notes
      setLoadingNotes(true);
      try {
        const notesRes = await applicationsAPI.getNotes(id);
        setNotes(notesRes.data || []);
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setLoadingNotes(false);
      }
    } catch (error) {
      toast.error('Failed to load application');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await applicationsAPI.updateStatus(id, newStatus);
      toast.success('Application status updated');
      fetchApplication();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSendingNote(true);
    try {
      await applicationsAPI.addNote(id, {
        content: newNote,
        is_internal: isInternalNote
      });
      toast.success(isInternalNote ? 'Internal note added' : 'Message sent to student');
      setNewNote('');
      setIsInternalNote(false);
      
      // Refresh notes
      const notesRes = await applicationsAPI.getNotes(id);
      setNotes(notesRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSendingNote(false);
    }
  };

  const handleExportPDF = () => {
    const app = application;
    const doc = new jsPDF();
    const lineHeight = 7;
    let y = 20;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(234, 179, 8);
    doc.text('H E Direct Consultants', 105, y, { align: 'center' });
    y += 10;

    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('Application Details', 105, y, { align: 'center' });
    y += 15;

    // Application ID
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`Application ID: ${app.id}`, 20, y);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 190, y, { align: 'right' });
    y += 15;

    // Student Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Student Information', 20, y);
    y += 2;
    doc.setDrawColor(234, 179, 8);
    doc.setLineWidth(0.5);
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);
    
    doc.text(`Name: ${app.student_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Email: ${app.student_email || 'N/A'}`, 20, y);
    y += lineHeight;
    if (app.agent_name) {
      doc.text(`Referred by Agent: ${app.agent_name}`, 20, y);
      y += lineHeight;
    }
    y += 8;

    // Course Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Course Information', 20, y);
    y += 2;
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);

    doc.text(`Course: ${app.course_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`University: ${app.university_name || 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Country: ${app.country || 'N/A'}`, 20, y);
    y += lineHeight + 8;

    // Application Status
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50);
    doc.text('Application Status', 20, y);
    y += 2;
    doc.line(20, y, 100, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const statusColors = {
      enrolled: [34, 197, 94],
      accepted: [59, 130, 246],
      rejected: [239, 68, 68],
      under_review: [234, 179, 8],
      submitted: [249, 115, 22]
    };
    const statusColor = statusColors[app.status] || [100, 100, 100];
    doc.setTextColor(...statusColor);
    doc.text(`Status: ${app.status?.replace('_', ' ').toUpperCase() || 'N/A'}`, 20, y);
    y += lineHeight;

    doc.setTextColor(80);
    doc.text(`Journey Stage: ${app.journey_stage?.replace(/_/g, ' ') || 'Inquiry'}`, 20, y);
    y += lineHeight;
    doc.text(`Created: ${app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB') : 'N/A'}`, 20, y);
    y += lineHeight;
    doc.text(`Last Updated: ${app.updated_at ? new Date(app.updated_at).toLocaleDateString('en-GB') : 'N/A'}`, 20, y);
    y += lineHeight + 8;

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        'H E Direct Consultants | amin@he-direct.com | +44 7488 420729',
        105, 290, { align: 'center' }
      );
      doc.text(`Page ${i} of ${pageCount}`, 190, 290, { align: 'right' });
    }

    const fileName = `HEDirect_Application_${app.student_name?.replace(/\s+/g, '_') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('PDF exported successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      under_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      accepted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      enrolled: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex items-center justify-center transition-colors">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900">
        <Navbar />
        <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Application Not Found</h2>
            <Button onClick={() => navigate(-1)} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button & Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-white">Application Details</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">ID: {application.id?.slice(0, 12)}...</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExportPDF}
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              {isAdmin && (
                <Select value={application.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-40 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    <SelectItem value="draft" className="text-gray-800 dark:text-white">Draft</SelectItem>
                    <SelectItem value="submitted" className="text-gray-800 dark:text-white">Submitted</SelectItem>
                    <SelectItem value="under_review" className="text-gray-800 dark:text-white">Under Review</SelectItem>
                    <SelectItem value="accepted" className="text-gray-800 dark:text-white">Accepted</SelectItem>
                    <SelectItem value="rejected" className="text-gray-800 dark:text-white">Rejected</SelectItem>
                    <SelectItem value="enrolled" className="text-gray-800 dark:text-white">Enrolled</SelectItem>
                    <SelectItem value="visa_applied" className="text-gray-800 dark:text-white">Visa Applied</SelectItem>
                    <SelectItem value="visa_successful" className="text-gray-800 dark:text-white">Visa Successful</SelectItem>
                    <SelectItem value="visa_rejected" className="text-gray-800 dark:text-white">Visa Rejected</SelectItem>
                    <SelectItem value="disappeared" className="text-gray-800 dark:text-white">Disappeared</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Phase D — Visa Success Probability AI widget */}
          {!isStudent && application.student_id && (
            <>
              {application.needs_review && (
                <div className="mb-4 rounded-lg border-2 border-rose-400 bg-rose-50 dark:bg-rose-900/30 p-3 flex items-start justify-between gap-3" data-testid="needs-review-banner">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-300 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-rose-700 dark:text-rose-200">Application flagged for review</p>
                      <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{application.needs_review_reason || 'High-risk visa profile detected.'}</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/visa-probability/clear-review/${application.id}`);
                        toast.success('Review flag cleared');
                        fetchApplication();
                      } catch (e) {
                        toast.error('Failed to clear flag');
                      }
                    }}
                    className="text-xs px-3 py-1.5 rounded bg-white hover:bg-rose-100 text-rose-700 border border-rose-300 shrink-0"
                    data-testid="clear-review-btn"
                  >
                    Mark triaged
                  </button>
                </div>
              )}
              <VisaProbabilityWidget studentId={application.student_id} applicationId={application.id} />
            </>
          )}

          {/* Phase A — Documents Required at-a-glance banner */}
          {!isStudent && application.student_id && (
            <DocumentsRequiredBanner studentId={application.student_id} />
          )}

          {/* Application Journey - Prominent at the top */}
          <div className="bg-white dark:bg-slate-800 border-2 border-teal-200 dark:border-teal-800 rounded-2xl p-6 mb-6" data-testid="journey-section">
            <JourneyTracker 
              application={application} 
              userRole={user?.role || 'student'}
              onStageUpdate={fetchApplication}
            />
          </div>

          {/* Journey Documents & Notes */}
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 mb-6">
            <JourneyDocumentManager
              application={application}
              onUpdate={fetchApplication}
              isAdmin={isAdmin}
            />
          </div>

          {/* Phase A — Student's personal documents (passport / CV / transcript / etc.) */}
          {!isStudent && application.student_id && (
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 mb-6">
              <StudentPersonalDocsPanel
                studentId={application.student_id}
                studentName={application.student_name}
              />
            </div>
          )}

          {/* Application Info Grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Student Information */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600" />
                Student Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400/20 to-teal-600/20 flex items-center justify-center">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-xl">
                      {application.student_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-white text-lg">{application.student_name}</p>
                    <Badge className={getStatusColor(application.status)}>
                      {application.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{application.student_email || 'No email'}</span>
                  </div>
                  {application.student_phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{application.student_phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Applied: {new Date(application.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {application.agent_name && (
                  <div className="pt-3 mt-3 border-t border-gray-200 dark:border-slate-600">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Referred by Agent</p>
                    <p className="font-medium text-purple-600 dark:text-purple-400">{application.agent_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Course Information */}
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-teal-600" />
                Course Information
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Course Name</p>
                  <p className="font-semibold text-gray-800 dark:text-white">{application.course_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">{application.university_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">{application.country}</span>
                </div>
                {application.tuition_fee && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">
                      £{application.tuition_fee?.toLocaleString()} / year
                    </span>
                  </div>
                )}
                {application.course_url && (
                  <a
                    href={application.course_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Course Page
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Unified Activity Feed — admin / agent only */}
          {!isStudent && (
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6 mb-6">
              <ActivityFeedTimeline entityType="application" entityId={application.id} maxHeight="28rem" />
            </div>
          )}

          {/* Notes & Communication */}
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-teal-600" />
              Notes & Communication
            </h3>
            {/* Add Note Form */}
            {(isAdmin || isAgent) && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note or message..."
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mb-3"
                  rows={3}
                  data-testid="note-input"
                />
                <div className="flex items-center justify-between">
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal-note"
                        checked={isInternalNote}
                        onCheckedChange={setIsInternalNote}
                      />
                      <Label htmlFor="internal-note" className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                        Internal note (not visible to student)
                      </Label>
                    </div>
                  )}
                  <Button
                    onClick={handleAddNote}
                    disabled={sendingNote || !newNote.trim()}
                    className="bg-teal-600 text-white hover:bg-teal-700"
                    data-testid="send-note-btn"
                  >
                    {sendingNote ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {isInternalNote ? 'Add Note' : 'Send Message'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Notes List */}
            <div className="space-y-3">
              {loadingNotes ? (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 text-teal-600 animate-spin mx-auto" />
                </div>
              ) : notes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No notes yet</p>
              ) : (
                notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-4 rounded-lg ${
                      note.is_internal 
                        ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' 
                        : 'bg-gray-50 dark:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 dark:text-white text-sm">
                          {note.author_name || 'System'}
                        </span>
                        {note.is_internal && (
                          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs">
                            Internal
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApplicationDetailPage;
